import { useState, useEffect, useCallback } from "react";
import { DateYr } from "../lib/shortDate";
import { fmtMoneyExact, Money } from "@/lib/money";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, th as palette, I, Over, Pill, Btn } from "@/lib/bricks";
import AddressAutocomplete, { mapProvince, GeocodedMapCutout, readCoords } from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { startGoogleCalendarOAuth } from "@/lib/googleCalendarConnect";
import { useListingMedia } from "@/lib/listingMedia";
import { BookMediaButton, ListingMediaPrompt } from "@/components/listing/BookMedia";
import InfoTip from "@/components/ui/info-tip";
import DealRoomTab from "@/components/deal/DealRoomTab";
import { Stats, StatusTabs, SmallSelect, Section, List, Row, Empty, Loading, Meta, StatusText, humanize } from "@/components/calm/Calm";

const KIND_TONE = { showing:'brick', closing:'ok', shoot:'warn', custom:'neutral' };
const ENV_TONE = { draft:'neutral', sent:'brick', completed:'ok', declined:'brick', expired:'warn', voided:'neutral' };
const REQ_TONE = { approved:'ok', exempt:'neutral', pending:'warn', rejected:'brick', missing:'neutral' };
const REQ_LABEL = { approved:'Approved', exempt:'Not applicable', pending:'In review', rejected:'Sent back', missing:'Missing' };
const REVIEW_LABEL = { submitted:'filed', approved:'approved', rejected:'sent back', exempted:'marked not applicable', exemption_removed:'made required again' };

const fmtCAD = fmtMoneyExact;
function fmtDate(d) {
  if (!d) return '—';
  // Date-only values are calendar days; parse them locally so they don't show a day early.
  const v = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
  return new Date(v).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}
function fmtDay(d) {
  if (!d) return '—';
  const v = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
  const dt = new Date(v);
  const opts = dt.getFullYear() === new Date().getFullYear() ? { month:'short', day:'numeric' } : { year:'numeric', month:'short', day:'numeric' };
  return dt.toLocaleDateString('en-CA', opts);
}
function fmtWhen(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString('en-CA', { year:'numeric', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' });
}
function contactName(c) {
  if (!c) return '';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim();
}

const TABS = ['Overview', 'Conditions', 'Calendar', 'Documents', 'Checklist', 'E-sign'];
const ROOM_TAB = 'Deal room'; // only when DEAL_ROOM is on
const TAB_CSS = '.txd-tabsel{display:block}.txd-tabs{display:none}@media (min-width:640px){.txd-tabsel{display:none}.txd-tabs{display:block}}';
const COND_TONE = { open: 'neutral', due_soon: 'warn', overdue: 'brick', fulfilled: 'ok', waived: 'ok', lapsed: 'brick' };
const COND_STATE_LABEL = { open: 'Open', due_soon: 'Due soon', overdue: 'Deadline passed', fulfilled: 'Fulfilled', waived: 'Waived', lapsed: 'Lapsed' };
const COND_KINDS = [
  ['financing', 'Financing'], ['inspection', 'Home inspection'], ['status_certificate', 'Status certificate review'],
  ['sale_of_property', "Sale of buyer's property"], ['lawyer_review', 'Lawyer review'], ['insurance', 'Insurance'], ['other', 'Other condition'],
];
function fmtDeadline(local) {
  if (!local) return '—';
  const [d, t] = local.split('T');
  const dt = new Date(`${d}T00:00:00`);
  const day = dt.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const [h, m] = (t || '23:59').split(':').map(Number);
  const hh = ((h + 11) % 12) + 1;
  return `${day}, ${hh}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'} ET`;
}
function fmtLeft(sec) {
  if (sec == null) return '';
  const abs = Math.abs(sec);
  const d = Math.floor(abs / 86400), h = Math.floor((abs % 86400) / 3600);
  const span = d > 0 ? `${d}d ${h}h` : (h > 0 ? `${h}h` : `${Math.max(1, Math.floor(abs / 60))}m`);
  return sec >= 0 ? `${span} left` : `${span} ago`;
}

export default function TransactionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { user, session } = useAuth();
  const { data: listingMedia } = useListingMedia({ dealId: id });
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t) || t === ROOM_TAB ? t : 'Overview';
  });
  const [roomOn, setRoomOn] = useState(false);
  const tabs = roomOn ? [...TABS, ROOM_TAB] : TABS;
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [docs, setDocs] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', type: 'General', requirement_id: '' });
  const [comp, setComp] = useState(null);
  const [rejectDoc, setRejectDoc] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [exemptReq, setExemptReq] = useState(null);
  const [exemptReason, setExemptReason] = useState('');
  const [compBusy, setCompBusy] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState('');

  const [checklist, setChecklist] = useState([]);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [checkForm, setCheckForm] = useState({ title: '', due_date: '', assigned_to: '' });

  const [envelopes, setEnvelopes] = useState([]);
  const [showEnvModal, setShowEnvModal] = useState(false);
  const [envForm, setEnvForm] = useState({ title: '', message: '' });
  const [envRecipients, setEnvRecipients] = useState([{ name: '', email: '', role: 'Signer' }]);
  const [sendingId, setSendingId] = useState('');
  const [docusign, setDocusign] = useState(null);
  const [showDsModal, setShowDsModal] = useState(false);
  const [dsForm, setDsForm] = useState({ document_id: '', title: '', message: '', send_now: false });
  const [dsRecipients, setDsRecipients] = useState([{ name: '', email: '', role: 'Signer' }]);
  const [dsBusy, setDsBusy] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [conds, setConds] = useState({ conditions: [], summary: null });
  const [showCondModal, setShowCondModal] = useState(false);
  const [condForm, setCondForm] = useState({ kind: 'financing', label: '', date: '', time: '23:59', notes: '' });
  const [resolveCond, setResolveCond] = useState(null);
  const [resolveForm, setResolveForm] = useState({ outcome: 'fulfilled', document_id: '', note: '', mark_deal_fallen_through: false });
  const [extendCond, setExtendCond] = useState(null);
  const [extendForm, setExtendForm] = useState({ date: '', time: '23:59' });
  const [condBusy, setCondBusy] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [showCalModal, setShowCalModal] = useState(false);
  const [calForm, setCalForm] = useState({ kind: 'showing', title: '', starts_at: '', ends_at: '', location: '' });
  const [gcal, setGcal] = useState(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = async () => {
    const { data: { session: live } } = await supabase.auth.getSession();
    const token = live?.access_token || session?.access_token;
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  useEffect(() => {
    let live = true;
    fetch(`${BASE}/api/deal-room/enabled`).then(r => (r.ok ? r.json() : { enabled: false }))
      .then(d => { if (!live) return; setRoomOn(!!d?.enabled); if (!d?.enabled) setActiveTab(t => (t === ROOM_TAB ? 'Overview' : t)); })
      .catch(() => { if (live) setActiveTab(t => (t === ROOM_TAB ? 'Overview' : t)); });
    return () => { live = false; };
  }, [BASE]);


  const load = useCallback(() => {
    (async () => {
      const headers = await getAuthHeaders();
      fetch(`${BASE}/api/transactions/${id}`, { headers })
        .then(r => r.json()).then(d => { setTx(d); setEditForm(d); setLoading(false); })
        .catch(() => setLoading(false));
      fetch(`${BASE}/api/transactions/${id}/documents`, { headers }).then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => {});
      fetch(`${BASE}/api/transactions/${id}/compliance`, { headers }).then(r => r.ok ? r.json() : null).then(d => { if (d && Array.isArray(d.items)) setComp(d); }).catch(() => {});
      fetch(`${BASE}/api/transactions/${id}/checklist`, { headers }).then(r => r.json()).then(d => setChecklist(Array.isArray(d) ? d : [])).catch(() => {});
      fetch(`${BASE}/api/transactions/${id}/conditions`, { headers }).then(r => r.ok ? r.json() : null).then(d => { if (d && Array.isArray(d.conditions)) setConds(d); }).catch(() => {});
      fetch(`${BASE}/api/transactions/${id}/envelopes`, { headers }).then(r => r.json()).then(d => setEnvelopes(Array.isArray(d) ? d : [])).catch(() => {});
      fetch(`${BASE}/api/transactions/${id}/calendar`, { headers }).then(r => r.json()).then(d => setEvents(Array.isArray(d) ? d : [])).catch(() => {});
      fetch(`${BASE}/api/crm/contacts`, { headers }).then(r => r.json()).then(d => setContacts(Array.isArray(d) ? d : [])).catch(() => {});
      fetch(`${BASE}/api/integrations`, { headers }).then(r => r.json()).then(d => {
        const tool = (d.tools || []).find(t => t.kind === 'calendar');
        setGcal({ ...(d.google || {}), tool });
        setDocusign(d.docusign || null);
      }).catch(() => {});
    })();
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const apiError = async (res, fallback) => {
    const body = await res.json().catch(() => ({}));
    const d = body.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d.message === 'string') return d.message;
    return fallback;
  };

  const handleSave = async () => {
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}`, { method: 'PATCH', headers, body: JSON.stringify(editForm) });
      if (!res.ok) throw new Error();
      const saved = await res.json().catch(() => ({}));
      toast.success("Listing updated");
      if (saved?.commission?.status === 'not_recorded') {
        toast.error(`Commission not recorded: ${saved.commission.detail}`);
      } else if (saved?.commission?.status === 'locked') {
        toast.warning(saved.commission.detail);
      } else if (saved?.commission?.status === 'updated') {
        toast.message("Commission updated to match the deal.");
      }
      if (saved?.compliance_summary && !saved.compliance_summary.complete) {
        const miss = saved.compliance_summary.missing_names || [];
        toast.warning(`Closed, but the deal file isn't complete${miss.length ? `: still needs ${miss.join(', ')}` : ' (documents still in review)'}.`);
      }
      setEditing(false);
      load();
    } catch { toast.error("Failed to update listing"); }
  };

  const handleAttachContact = async (contactId) => {
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ contact_id: contactId || null }) });
      if (!res.ok) throw new Error();
      toast.success(contactId ? "Contact attached" : "Contact removed");
      load();
    } catch { toast.error("Could not update contact"); }
  };

  const handleAddDoc = async (e) => {
    e?.preventDefault?.();
    const headers = await getAuthHeaders();
    const form = e?.currentTarget instanceof HTMLFormElement ? e.currentTarget : null;
    const fd = form ? new FormData(form) : null;
    const name = String(fd?.get('name') ?? docForm.name ?? '').trim();
    const type = String(fd?.get('type') ?? docForm.type ?? 'General');
    if (!docFile) {
      setDocError('Choose a file to upload');
      return;
    }
    if (!name) {
      setDocError('Document name required');
      return;
    }
    setDocError('');
    setDocUploading(true);
    try {
      const prep = await fetch(`${BASE}/api/documents/upload-url`, {
        method: 'POST', headers,
        body: JSON.stringify({
          filename: docFile.name,
          content_type: docFile.type || 'application/octet-stream',
          file_size: docFile.size,
          transaction_id: id,
          name,
          for_esign: false,
        }),
      });
      const prepBody = await prep.json().catch(() => ({}));
      if (!prep.ok) throw new Error(typeof prepBody.detail === 'string' ? prepBody.detail : 'Failed to prepare upload');
      const { upload_url, storage_path } = prepBody;
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        body: docFile,
        headers: { 'Content-Type': docFile.type || 'application/octet-stream' },
      });
      if (!putRes.ok) throw new Error('Failed to upload file bytes');
      const res = await fetch(`${BASE}/api/transactions/${id}/documents`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, file_url: storage_path, type, requirement_id: docForm.requirement_id || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof body.detail === 'string' ? body.detail : 'Failed to add document';
        throw new Error(detail);
      }
      toast.success('Document uploaded');
      setShowDocModal(false);
      setDocForm({ name: '', type: 'General', requirement_id: '' });
      setDocFile(null);
      setDocError('');
      load();
    } catch (err) {
      setDocError(err.message || 'Failed to upload document');
      toast.error(err.message || 'Failed to upload document');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocAction = async (docId, action, note) => {
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}/documents/${docId}/${action}`, { method: 'POST', headers, body: JSON.stringify({ note: note || null }) });
      if (!res.ok) { toast.error(await apiError(res, `Failed to ${action} document`)); return false; }
      toast.success(action === 'approve' ? 'Document approved' : 'Sent back to the agent');
      load();
      return true;
    } catch { toast.error(`Failed to ${action} document`); return false; }
  };

  const compCall = async (path, method, body, okMsg) => {
    setCompBusy(true);
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}${path}`, { method, headers: await getAuthHeaders(), ...(body ? { body: JSON.stringify(body) } : {}) });
      if (!res.ok) { toast.error(await apiError(res, 'Could not save')); return false; }
      if (okMsg) toast.success(okMsg);
      load();
      return true;
    } catch { toast.error('Could not save'); return false; }
    finally { setCompBusy(false); }
  };
  const openDocUpload = (requirement) => {
    setDocForm({ name: requirement?.name || '', type: requirement?.doc_kind || 'General', requirement_id: requirement?.requirement_id || '' });
    setDocFile(null); setDocError(''); setShowDocModal(true);
  };

  const handleAddCheckItem = async () => {
    if (!checkForm.title) return toast.error("Title required");
    const headers = await getAuthHeaders();
    try {
      await fetch(`${BASE}/api/transactions/${id}/checklist`, { method: 'POST', headers, body: JSON.stringify(checkForm) });
      toast.success("Checklist item added");
      setShowCheckModal(false);
      setCheckForm({ title: '', due_date: '', assigned_to: '' });
      load();
    } catch { toast.error("Failed to add checklist item"); }
  };

  const condCall = async (path, method, body, ok) => {
    setCondBusy(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BASE}/api/transactions/${id}/conditions${path}`, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) { toast.error(await apiError(res, 'Could not save the condition')); return false; }
      toast.success(ok);
      load();
      return true;
    } catch { toast.error('Could not save the condition'); return false; } finally { setCondBusy(false); }
  };

  const handleAddCondition = async () => {
    if (!condForm.date) return toast.error('Pick the deadline date');
    const done = await condCall('', 'POST', {
      kind: condForm.kind, label: condForm.label || null, notes: condForm.notes || null,
      deadline: `${condForm.date}T${condForm.time || '23:59'}`,
    }, 'Condition added');
    if (done) { setShowCondModal(false); setCondForm({ kind: 'financing', label: '', date: '', time: '23:59', notes: '' }); }
  };

  const handleResolveCondition = async () => {
    if (!resolveCond) return;
    const label = { fulfilled: 'Marked fulfilled', waived: 'Marked waived', lapsed: 'Marked lapsed' }[resolveForm.outcome];
    const done = await condCall(`/${resolveCond.id}/resolve`, 'POST', {
      outcome: resolveForm.outcome, document_id: resolveForm.document_id || null, note: resolveForm.note || null,
      mark_deal_fallen_through: resolveForm.outcome === 'lapsed' && resolveForm.mark_deal_fallen_through,
    }, label);
    if (done) setResolveCond(null);
  };

  const handleExtendCondition = async () => {
    if (!extendCond || !extendForm.date) return toast.error('Pick the new deadline date');
    const done = await condCall(`/${extendCond.id}`, 'PATCH', { deadline: `${extendForm.date}T${extendForm.time || '23:59'}` }, 'Deadline updated');
    if (done) setExtendCond(null);
  };

  const handleToggleCheck = async (itemId, done) => {
    const headers = await getAuthHeaders();
    try {
      await fetch(`${BASE}/api/transactions/${id}/checklist/${itemId}`, { method: 'PATCH', headers, body: JSON.stringify({ completed: !done }) });
      load();
    } catch { toast.error("Failed to update item"); }
  };

  const handleCreateEnvelope = async () => {
    if (!envForm.title) return toast.error("Title required");
    if (envRecipients.some(r => !r.name.trim() || !r.email.trim())) return toast.error("Each recipient needs a name and email");
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...envForm, transaction_id: id, recipients: envRecipients }),
      });
      if (!res.ok) {
        toast.error(await apiError(res, "Failed to create envelope"));
        return;
      }
      toast.success("Envelope saved as draft");
      setShowEnvModal(false);
      setEnvForm({ title: '', message: '' });
      setEnvRecipients([{ name: '', email: '', role: 'Signer' }]);
      load();
    } catch { toast.error("Failed to create envelope"); }
  };

  const handleSendEnvelope = async (envId) => {
    setSendingId(envId);
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${envId}/send`, { method: 'POST', headers });
      const body = await res.json().catch(() => ({}));
      const d = body.detail;
      if (!res.ok) {
        const msg = typeof d === 'string' ? d : d?.message || "Could not send envelope.";
        toast.error(msg);
        load();
        return;
      }
      if (body.emailed) toast.success("Envelope emailed for signing");
      else toast.message(body.hint || "Signing links ready to copy. No email sent.");
      load();
    } catch {
      toast.error("Could not send envelope.");
    } finally {
      setSendingId('');
    }
  };

  useEffect(() => {
    if (searchParams.get('docusign') !== 'prepared') return;
    toast.message("Back from DocuSign. Tap Check DocuSign for the latest status.");
    setActiveTab('E-sign');
    setSearchParams({}, { replace: true });
  }, [searchParams]); // eslint-disable-line

  const openDocuSignSend = () => {
    const prefill = party?.email ? [{ name: contactName(party), email: party.email, role: 'Signer' }] : [{ name: '', email: '', role: 'Signer' }];
    const firstPdf = docs.find(d => /\.pdf($|\?)/i.test(String(d.name || '')) || /\.pdf($|\?)/i.test(String(d.file_url || '')));
    setDsRecipients(prefill);
    setDsForm({ document_id: firstPdf?.id || docs[0]?.id || '', title: tx.property_address ? `Please sign: ${tx.property_address}` : '', message: '', send_now: false });
    setShowDsModal(true);
  };

  const handleDocuSignSend = async () => {
    if (!dsForm.document_id) return toast.error("Pick a PDF from this deal's documents");
    if (dsRecipients.some(r => !r.name.trim() || !r.email.trim())) return toast.error("Each signer needs a name and email");
    setDsBusy('send');
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/integrations/docusign/send`, {
        method: 'POST', headers,
        body: JSON.stringify({
          transaction_id: id,
          document_id: dsForm.document_id,
          title: dsForm.title.trim() || null,
          message: dsForm.message.trim() || null,
          send_now: !!dsForm.send_now,
          signers: dsRecipients.map((r, i) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role, routing_order: i + 1 })),
        }),
      });
      if (!res.ok) {
        toast.error(await apiError(res, "DocuSign could not take this envelope."));
        return;
      }
      const body = await res.json().catch(() => ({}));
      setShowDsModal(false);
      if (body.prepare_url) {
        toast.message("Opening DocuSign to place signature fields…");
        window.location.href = body.prepare_url;
        return;
      }
      toast.success(body.hint || "Sent through DocuSign");
      load();
    } catch { toast.error("DocuSign could not take this envelope."); }
    finally { setDsBusy(''); }
  };

  const handleDocuSignAction = async (env, action) => {
    setDsBusy(`${action}-${env.id}`);
    const headers = await getAuthHeaders();
    const path = action === 'status'
      ? `/api/integrations/docusign/envelopes/${env.id}/status`
      : `/api/integrations/docusign/envelopes/${env.id}/${action}`;
    try {
      const res = await fetch(`${BASE}${path}`, { method: action === 'status' ? 'GET' : 'POST', headers });
      if (!res.ok) {
        toast.error(await apiError(res, "Could not reach DocuSign."));
        load();
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (action === 'prepare' && body.url) {
        window.location.href = body.url;
        return;
      }
      if (body.filed && !body.filed.already_filed) toast.success("Signed PDF filed in this deal's documents");
      else toast.success(`DocuSign status: ${body.external_status || body.status}`);
      load();
    } catch { toast.error("Could not reach DocuSign."); }
    finally { setDsBusy(''); }
  };

  const handleFileSignedRecord = async (env) => {
    setDsBusy(`record-${env.id}`);
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${env.id}/signed-record`, { method: 'POST', headers });
      if (!res.ok) {
        toast.error(await apiError(res, "Could not save the signed record yet."));
        return;
      }
      toast.success("Signed record filed in this deal's documents");
      load();
    } catch { toast.error("Could not save the signed record yet."); }
    finally { setDsBusy(''); }
  };

  const copyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Signing link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleAddEvent = async () => {
    if (!calForm.starts_at) return toast.error("Start time required");
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}/calendar`, {
        method: 'POST', headers,
        body: JSON.stringify({
          ...calForm,
          starts_at: new Date(calForm.starts_at).toISOString(),
          ends_at: calForm.ends_at ? new Date(calForm.ends_at).toISOString() : null,
        }),
      });
      if (!res.ok) {
        toast.error(await apiError(res, "Could not add event"));
        return;
      }
      toast.success("Event added");
      setShowCalModal(false);
      setCalForm({ kind: 'showing', title: '', starts_at: '', ends_at: '', location: '' });
      load();
    } catch { toast.error("Could not add event"); }
  };

  const handleDeleteEvent = async (eventId) => {
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/transactions/${id}/calendar/${eventId}`, { method: 'DELETE', headers });
      if (!res.ok) {
        toast.error(await apiError(res, "Could not remove event"));
        return;
      }
      load();
    } catch { toast.error("Could not remove event"); }
  };

  const connectGoogle = async () => {
    const headers = await getAuthHeaders();
    try {
      const res = await fetch(`${BASE}/api/integrations/google-calendar/start`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === 'string' ? body.detail : "Google Calendar permission is not granted yet.");
        return;
      }
      const started = await startGoogleCalendarOAuth(body);
      if (started.redirected) return;
      toast.error(started.error || "Google Calendar permission is not granted yet.");
    } catch {
      toast.error("Google Calendar permission is not granted yet.");
    }
  };

  const inputStyle = { marginTop: 8, width: '100%', minHeight: 44, padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' };
  const roStyle = { ...inputStyle, background: p.surf2, color: p.ink2, cursor: 'default' };
  const condSummary = conds.summary || tx?.conditions_summary || null;
  const condRows = conds.conditions || [];
  const party = tx?.contact;

  if (loading) return <Loading />;
  if (!tx) return (
    <div style={{ padding: 48, textAlign: 'center', color: p.ink3, fontFamily: F.display }}>
      Listing not found. <span style={{ color: p.ink2, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }} onClick={() => navigate('/transactions')}>Back to listings</span>
    </div>
  );

  const showingClosings = events.filter(e => e.kind === 'showing' || e.kind === 'closing' || e.kind === 'custom');

  const condAttention = !!(condSummary && (condSummary.overdue || condSummary.lapsed));
  const statusLine = [
    humanize(tx.status),
    tx.deal_type,
    condSummary && condSummary.total > 0 ? (condSummary.firm ? 'Firm' : (condSummary.overdue ? 'Condition deadline passed' : (condSummary.lapsed ? 'Condition lapsed' : 'Conditional'))) : null,
    comp?.summary && comp.summary.required_total > 0 ? (comp.summary.complete ? 'File complete' : `${comp.summary.satisfied} of ${comp.summary.required_total} documents`) : null,
  ].filter(Boolean);
  const commissionValue = tx.commission_amount != null && tx.commission_amount !== '' ? fmtCAD(tx.commission_amount) : (tx.commission_rate ? `${tx.commission_rate}%` : '—');
  const linkBtn = { background: 'none', border: 'none', padding: 0, minHeight: 44, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', fontFamily: F.display, fontSize: 13, color: p.ink2, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS, whiteSpace: 'nowrap' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, fontFamily: F.display, color: p.ink, maxWidth: 1040, minWidth: 0 }}>
      <style>{TAB_CSS}</style>

      <div>
        <button onClick={() => navigate('/transactions')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.display, fontSize: 13, color: p.ink3, display: 'flex', alignItems: 'center', gap: 6, padding: 0, minHeight: 44, marginTop: -12, marginBottom: 4 }}>
          <I name="arrL" size={14} color={p.ink3}/> All listings
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, lineHeight: 1.15, margin: 0, overflowWrap: 'anywhere' }}>{tx.property_address}</h1>
            <div style={{ marginTop: 8 }}><StatusText attention={condAttention}>{statusLine.join(' · ')}</StatusText></div>
            <div style={{ fontSize: 13, color: p.ink3, marginTop: 4 }}>
              {tx.city}{tx.city && tx.province ? ', ' : ''}{tx.province}
              {tx.mls_number ? ` · MLS# ${tx.mls_number}` : ''}
              {tx.closing_date ? ` · Closes ${fmtDate(tx.closing_date)}` : ''}
              {party ? ` · ${contactName(party)}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {editing ? (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => { setEditing(false); setEditForm(tx); }}>Cancel</Btn>
                <Btn variant="primary" dark={dark} onClick={handleSave}>Save changes</Btn>
              </>
            ) : (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => setEditing(true)}>Edit</Btn>
                <BookMediaButton dealId={id} summary={listingMedia} dark={dark} />
              </>
            )}
          </div>
        </div>
      </div>

      <ListingMediaPrompt dealId={id} summary={listingMedia} dark={dark} p={p} onOpenShoot={(pid) => navigate(`/properties/${pid}`)} />

      <Stats items={[
        { label: 'Sale price', value: <Money value={tx.sale_price || tx.list_price} /> },
        { label: 'Commission', value: commissionValue },
        { label: tx.acceptance_date ? `Closing · accepted ${fmtDate(tx.acceptance_date)}` : 'Closing', value: fmtDay(tx.closing_date) },
      ]} />

      <Section title="Contact" action={
        <select aria-label="Contact on this listing"
          value={tx.contact_id || ''}
          onChange={e => handleAttachContact(e.target.value)}
          style={{ padding: '6px 10px', minHeight: 44, border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none', maxWidth: 220, minWidth: 0 }}
        >
          <option value="">{party ? 'Remove contact' : 'Attach a contact'}</option>
          {contacts.map(c => (
            <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.email ? ` · ${c.email}` : ''}</option>
          ))}
        </select>
      }>
        {!party ? (
          <Empty>No contact on this listing yet. Attach one from your contacts.</Empty>
        ) : (
          <List>
            <Row title={contactName(party)} meta={humanize(party.type) || 'Contact'}
              next={<Meta parts={[party.email, party.phone].filter(Boolean).map(v => ({ text: v, figure: true }))} />} />
          </List>
        )}
      </Section>

      <Section title="Shoots">
        {(!tx.shoots || tx.shoots.length === 0) ? (
          <Empty>No shoot on this listing yet.</Empty>
        ) : (
          <List>
            {(tx.shoots || []).map((s, i) => {
              const href = s.property_id ? `/properties/${s.property_id}` : '/jobs';
              return (
                <Row key={s.id || i} onClick={() => navigate(href)}
                  title={s.address || 'Shoot'}
                  meta={s.package || null}
                  next={s.scheduled_date ? <span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink3 }}><DateYr d={s.scheduled_date} /></span> : null}
                  status={humanize(s.status)} />
              );
            })}
          </List>
        )}
      </Section>

      <div className="txd-tabs"><StatusTabs label="Sections" testid="txd-tabs" options={tabs.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} hit /></div>
      <div className="txd-tabsel"><SmallSelect hit label="Section" value={activeTab} onChange={setActiveTab} options={tabs.map(t => ({ key: t, label: t }))} /></div>

      {activeTab === ROOM_TAB && roomOn && (
        <DealRoomTab dealId={id} base={BASE} getAuthHeaders={getAuthHeaders} dark={dark} p={p} inputStyle={inputStyle} onOpenTab={setActiveTab} />
      )}

      {activeTab === 'Overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '20px 16px', alignItems: 'end' }}>
            {[
              { label: 'Deal type', field: 'deal_type', type: 'select', opts: ['Purchase','Sale','Lease','Referral'] },
              { label: 'Status', field: 'status', type: 'select', opts: ['active','pending','completed','cancelled','fallen_through'] },
              { label: 'Property address', field: 'property_address', type: 'text', full: true },
              { label: 'City', field: 'city', type: 'text' },
              { label: 'Province', field: 'province', type: 'select', opts: ['ON','BC','AB','MB','SK','QC','NS','NB','NL','PE'] },
              { label: 'MLS number', field: 'mls_number', type: 'text' },
              { label: 'List price (CAD)', field: 'list_price', type: 'number' },
              { label: 'Sale price (CAD)', field: 'sale_price', type: 'number' },
              { label: 'Acceptance date', field: 'acceptance_date', type: 'date' },
              { label: 'Closing date', field: 'closing_date', type: 'date' },
              { label: 'Deposit due date', field: 'deposit_due_date', type: 'date' },
              { label: 'Co-operating brokerage', field: 'co_brokerage', type: 'text' },
              { label: 'Commission rate (%)', field: 'commission_rate', type: 'number' },
              { label: 'Flat commission (CAD, overrides rate)', field: 'commission_amount', type: 'number' },
              { label: 'Agent', field: 'agent_name', type: 'text', ro: true },
            ].map(({ label, field, type, opts, full, ro }) => (
              <div key={field} style={full ? { gridColumn: '1 / -1' } : {}}>
                <Over color={p.ink3}>{label}</Over>
                {field === 'property_address' ? (
                  editing ? (
                    <AddressAutocomplete
                      value={editForm.property_address || ''}
                      onChange={v => setEditForm(f => ({ ...f, property_address: v }))}
                      onSelect={s => setEditForm(f => ({
                        ...f,
                        property_address: s.label || f.property_address,
                        city: s.city || f.city,
                        province: mapProvince(s.province) || f.province,
                      }))}
                      placeholder="e.g. 123 Maple St"
                      p={p}
                      dark={dark}
                      lng={readCoords(tx)?.lng}
                      lat={readCoords(tx)?.lat}
                      style={inputStyle}
                    />
                  ) : (
                    <>
                      <input type="text" value={editForm.property_address || ''} readOnly style={roStyle}/>
                      <GeocodedMapCutout
                        address={tx.property_address}
                        city={tx.city}
                        province={tx.province}
                        lng={readCoords(tx)?.lng}
                        lat={readCoords(tx)?.lat}
                        dark={dark}
                      />
                    </>
                  )
                ) : type === 'select' ? (
                  <select value={editForm[field] || ''} onChange={e => editing && setEditForm({ ...editForm, [field]: e.target.value })}
                    disabled={!editing || ro}
                    style={{ ...inputStyle, background: (!editing || ro) ? p.surf2 : p.bg }}>
                    {opts.map(v => <option key={v} value={v}>{field === 'status' ? humanize(v) : v}</option>)}
                  </select>
                ) : (
                  <input type={type} value={editForm[field] || ''} onChange={e => editing && setEditForm({ ...editForm, [field]: e.target.value })}
                    readOnly={!editing || ro}
                    style={(!editing || ro) ? roStyle : inputStyle}/>
                )}
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <Over color={p.ink3}>Notes</Over>
              <textarea value={editForm.notes || ''} onChange={e => editing && setEditForm({ ...editForm, notes: e.target.value })} rows={4}
                readOnly={!editing}
                style={{ ...inputStyle, resize: 'vertical', background: !editing ? p.surf2 : 'transparent' }}/>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Conditions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 13, color: p.ink3, maxWidth: 560, display: 'flex', alignItems: 'center', gap: 8 }}>
              {condRows.length === 0
                ? 'No conditions yet.'
                : condSummary?.firm
                  ? 'Firm. All conditions fulfilled or waived.'
                  : `${condSummary?.open || 0} open of ${condRows.length}`}
              {!condSummary?.firm && (
                <InfoTip label="How conditions work">Add each condition from the offer with its deadline. The deal shows Firm once every condition is fulfilled or waived. Bricks never marks a condition lapsed on its own; record what happened.</InfoTip>
              )}
            </div>
            <Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowCondModal(true)}>Add condition</Btn>
          </div>
          <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
            {condRows.length === 0 && <div style={{ padding: '40px var(--bx-pad)', textAlign: 'center', fontSize: 13, color: p.ink3 }}>No conditions yet.</div>}
            {condRows.map((c, i) => {
              const isOpen = c.status === 'open';
              const doc = c.resolution_document_id ? docs.find(d => d.id === c.resolution_document_id) : null;
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px var(--bx-pad)', borderBottom: i === condRows.length - 1 ? 'none' : `1px solid ${p.hair}`, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 14, color: p.ink, fontWeight: 500 }}>{c.label}</div>
                      <Pill tone={COND_TONE[c.state] || 'neutral'} dark={dark}>{COND_STATE_LABEL[c.state] || c.state}</Pill>
                    </div>
                    <div style={{ fontSize: 12, color: p.ink3, marginTop: 4 }}>
                      Deadline {fmtDeadline(c.deadline_local)}{isOpen && c.seconds_left != null ? ` · ${fmtLeft(c.seconds_left)}` : ''}
                      {(c.deadline_history || []).length > 0 ? ` · extended ${(c.deadline_history || []).length}×` : ''}
                    </div>
                    {!isOpen && (
                      <div style={{ fontSize: 12, color: p.ink3, marginTop: 2 }}>
                        Recorded {fmtWhen(c.resolved_at)}{doc ? ` · ${doc.name}` : ''}{c.resolution_note ? ` · ${c.resolution_note}` : ''}
                      </div>
                    )}
                    {c.notes && <div style={{ fontSize: 12, color: p.ink4, marginTop: 2 }}>{c.notes}</div>}
                  </div>
                  {isOpen && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Btn variant="ghost" dark={dark} onClick={() => { setResolveCond(c); setResolveForm({ outcome: 'fulfilled', document_id: '', note: '', mark_deal_fallen_through: false }); }}>Record outcome</Btn>
                      <Btn variant="ghost" dark={dark} onClick={() => { setExtendCond(c); setExtendForm({ date: (c.deadline_local || '').slice(0, 10), time: (c.deadline_local || 'T23:59').slice(11, 16) || '23:59' }); }}>Change deadline</Btn>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'Calendar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: p.ink3 }}>
              Native calendar. Google Calendar is {gcal?.calendar_scope_granted ? 'connected' : 'not connected'}.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {gcal?.client_configured && !gcal?.calendar_scope_granted && (
                <Btn variant="ghost" dark={dark} onClick={connectGoogle}>
                  {gcal?.oauth_ready ? 'Connect Google Calendar' : 'Connect Google Calendar'}
                </Btn>
              )}
              <Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowCalModal(true)}>Add showing / closing</Btn>
            </div>
          </div>
          {gcal?.client_configured && !gcal?.oauth_ready && !gcal?.calendar_scope_granted && (
            <div style={{ fontSize: 12, color: p.ink3, padding: '10px 14px', border: `1px solid ${p.hair}`, borderRadius: 2 }}>
              Google Calendar isn’t connected. Events still save here.
            </div>
          )}
          <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
            {events.length === 0 && (
              <div style={{ padding: '40px var(--bx-pad)', textAlign: 'center', fontSize: 13, color: p.ink3 }}>
                No showings or closings on this listing yet
              </div>
            )}
            {events.map((ev, i) => (
              <div key={ev.id} className="bx-trow bx-1l" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1.6fr 1fr auto', padding: '16px var(--bx-pad)', borderBottom: i === events.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize: 13, alignItems: 'center', gap: 8 }}>
                <div className="bx-s"><Pill tone={KIND_TONE[ev.kind] || 'neutral'} dark={dark}>{ev.kind}</Pill></div>
                <div className="bx-t" style={{ fontWeight: 500 }}>{ev.title}</div>
                <div className="bx-x" style={{ fontFamily: F.mono, fontSize: 12, color: p.ink2 }}>{fmtWhen(ev.starts_at)}{ev.ends_at ? ` – ${fmtWhen(ev.ends_at)}` : ''}</div>
                <div className="bx-x" style={{ color: p.ink3 }}>{ev.location || '—'}</div>
                <div>
                  {ev.read_only ? (
                    <span style={{ fontSize: 12, color: p.ink4 }}>From shoot</span>
                  ) : (
                    <button onClick={() => handleDeleteEvent(ev.id)} style={linkBtn}>Remove</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {showingClosings.length === 0 && events.some(e => e.kind === 'shoot') && (
            <div style={{ fontSize: 12, color: p.ink4 }}>Shoot times come from the linked job.</div>
          )}
        </div>
      )}

      {activeTab === 'Documents' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {comp && comp.items.length > 0 && (
            <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
              <div style={{ padding: '16px var(--bx-pad)', borderBottom: `1px solid ${p.hair}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Required for this deal</div>
                  <div style={{ fontSize: 12, color: p.ink3, marginTop: 2 }}>
                    {comp.summary.complete
                      ? 'Complete.'
                      : `${comp.summary.satisfied} of ${comp.summary.required_total} done${comp.summary.pending ? ` · ${comp.summary.pending} in review` : ''}`}
                  </div>
                </div>
                <button type="button" onClick={() => navigate('/compliance')} style={linkBtn}>Compliance</button>
              </div>
              {comp.items.map((it, i) => (
                <div key={it.requirement_id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px var(--bx-pad)', borderBottom: i === comp.items.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize: 13, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontWeight: 500 }}>{it.name}</div>
                      {!it.required && <span style={{ fontSize: 12, color: p.ink4 }}>Optional</span>}
                    </div>
                    <div style={{ fontSize: 12, color: p.ink3, marginTop: 2 }}>
                      {it.state === 'exempt' && it.exemption ? `Not applicable: ${it.exemption.reason}` : null}
                      {it.document ? `${it.document.name}${it.document.reviewed_at ? ` · reviewed ${fmtDate(it.document.reviewed_at)}` : ''}` : null}
                      {it.state === 'missing' ? 'Nothing filed yet' : null}
                    </div>
                    {it.state === 'rejected' && it.document?.review_note && (
                      <div style={{ fontSize: 12, color: T.brick, marginTop: 2 }}>Sent back: {it.document.review_note}</div>
                    )}
                  </div>
                  <Pill tone={REQ_TONE[it.state] || 'neutral'} dark={dark}>{REQ_LABEL[it.state] || it.state}</Pill>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {it.state !== 'approved' && it.state !== 'exempt' && (
                      <button onClick={() => openDocUpload(it)} style={linkBtn}>{it.state === 'missing' ? 'Upload' : 'Upload new'}</button>
                    )}
                    {comp.can_review && it.state === 'pending' && it.document && (
                      <>
                        <button onClick={() => handleDocAction(it.document.id, 'approve')} style={linkBtn}>Approve</button>
                        <button onClick={() => { setRejectDoc(it.document); setRejectNote(''); }} style={linkBtn}>Send back</button>
                      </>
                    )}
                    {comp.can_review && it.required && (it.state === 'missing' || it.state === 'rejected') && (
                      <button onClick={() => { setExemptReq(it); setExemptReason(''); }} style={linkBtn}>Not applicable</button>
                    )}
                    {comp.can_review && it.state === 'exempt' && (
                      <button disabled={compBusy} onClick={() => compCall(`/compliance/${it.requirement_id}/exempt`, 'DELETE', null, 'Required again')} style={linkBtn}>Make required</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn variant="primary" dark={dark} icon="plus" onClick={() => openDocUpload(null)}>Upload document</Btn>
          </div>
          <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
            <div className="bx-thead" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', padding: '10px var(--bx-pad)', borderBottom: `1px solid ${p.hair}`, fontSize: 12, fontWeight: 500, color: p.ink4 }}>
              <div>Document</div><div>Type</div><div>Uploaded</div><div>Status</div><div/>
            </div>
            {docs.length === 0 && <div style={{ padding: '40px var(--bx-pad)', textAlign: 'center', fontSize: 13, color: p.ink3 }}>No documents uploaded yet.</div>}
            {docs.map((doc, i) => (
              <div key={doc.id} className="bx-trow bx-1l" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', padding: '16px var(--bx-pad)', borderBottom: i === docs.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize: 13, alignItems: 'center' }}>
                <div className="bx-t">
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" style={{ color: p.ink, textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>{doc.name}</a>
                  {comp && comp.items.length > 0 && (
                    <select value={doc.requirement_id || ''} disabled={compBusy}
                      onChange={e => compCall(`/documents/${doc.id}`, 'PATCH', { requirement_id: e.target.value || null }, e.target.value ? 'Filed under required document' : 'Removed from required documents')}
                      style={{ display: 'block', marginTop: 0, padding: '2px 8px', minHeight: 44, border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink3, background: p.bg, maxWidth: 260 }}>
                      <option value="">Not a required document</option>
                      {comp.items.map(it => <option key={it.requirement_id} value={it.requirement_id}>For: {it.name}</option>)}
                    </select>
                  )}
                  {doc.status === 'rejected' && doc.review_note && <div style={{ fontSize: 12, color: T.brick, marginTop: 2 }}>Sent back: {doc.review_note}</div>}
                </div>
                <div className="bx-x" style={{ color: p.ink3 }}>{doc.doc_type || doc.type || 'General'}</div>
                <div style={{ color: p.ink3, fontFamily: F.mono, fontSize: 12 }}><DateYr d={doc.created_at} /></div>
                <div className="bx-s"><Pill tone={doc.status === 'approved' ? 'ok' : doc.status === 'rejected' ? 'brick' : 'neutral'} dark={dark}>{doc.status || 'pending'}</Pill></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isManager && doc.status !== 'approved' && (
                    <button onClick={() => handleDocAction(doc.id, 'approve')} style={linkBtn}>Approve</button>
                  )}
                  {isManager && doc.status !== 'rejected' && (
                    <button onClick={() => { setRejectDoc(doc); setRejectNote(''); }} style={linkBtn}>Reject</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {comp?.log?.length > 0 && (
            <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
              <div style={{ padding: '12px var(--bx-pad)', borderBottom: `1px solid ${p.hair}`, fontSize: 13, fontWeight: 500 }}>Review history</div>
              {comp.log.slice(0, 12).map((r, i, arr) => {
                const reqName = comp.items.find(it => it.requirement_id === r.requirement_id)?.name;
                const docName = docs.find(d => d.id === r.document_id)?.name;
                return (
                  <div key={r.id} style={{ padding: '10px var(--bx-pad)', borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize: 12, color: p.ink3 }}>
                    <span style={{ color: p.ink2 }}>{r.actor_name || 'Someone'}</span> {REVIEW_LABEL[r.action] || r.action} {docName || reqName || 'a document'}
                    {r.self_review ? ' (own upload)' : ''}{r.note ? ` · ${r.note}` : ''} · {fmtWhen(r.created_at)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'Checklist' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: p.ink3 }}>{checklist.filter(c => c.completed || c.completed_at).length} of {checklist.length} complete</div>
            <Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowCheckModal(true)}>Add item</Btn>
          </div>
          <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
            {checklist.length === 0 && <div style={{ padding: '40px var(--bx-pad)', textAlign: 'center', fontSize: 13, color: p.ink3 }}>No checklist items yet.</div>}
            {checklist.map((item, i) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px var(--bx-pad)', borderBottom: i === checklist.length - 1 ? 'none' : `1px solid ${p.hair}` }}>
                <input type="checkbox" checked={!!(item.completed || item.completed_at)} onChange={() => handleToggleCheck(item.id, !!(item.completed || item.completed_at))}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: T.brick, flexShrink: 0 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: (item.completed || item.completed_at) ? p.ink3 : p.ink, textDecoration: (item.completed || item.completed_at) ? 'line-through' : 'none' }}>{item.title}</div>
                  {item.assigned_to && <div style={{ fontSize: 12, color: p.ink4, marginTop: 2 }}>Assigned to {item.assigned_to}</div>}
                </div>
                {item.due_date && (
                  <div style={{ fontFamily: F.mono, fontSize: 11, color: p.ink3 }}>{fmtDate(item.due_date)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'E-sign' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: p.ink3 }}>
              {docusign?.needs_reconnect ? 'Reconnect DocuSign in Connected tools.' : docusign?.connected ? `DocuSign connected${docusign.account_name ? ` · ${docusign.account_name}` : ''}` : 'Send documents for signature.'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {docusign?.connected && (
                <Btn variant="ghost" dark={dark} onClick={openDocuSignSend}>Send via DocuSign</Btn>
              )}
              <Btn variant="primary" dark={dark} icon="plus" onClick={() => {
                const prefill = party?.email ? [{ name: contactName(party), email: party.email, role: 'Signer' }] : [{ name: '', email: '', role: 'Signer' }];
                setEnvRecipients(prefill);
                setEnvForm({ title: tx.property_address ? `Agreement for ${tx.property_address}` : '', message: '' });
                setShowEnvModal(true);
              }}>New envelope</Btn>
            </div>
          </div>
          <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
            <div className="bx-thead" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '10px var(--bx-pad)', borderBottom: `1px solid ${p.hair}`, fontSize: 12, fontWeight: 500, color: p.ink4 }}>
              <div>Title</div><div>Recipients</div><div>Status</div><div>Created</div>
            </div>
            {envelopes.length === 0 && <div style={{ padding: '40px var(--bx-pad)', textAlign: 'center', fontSize: 13, color: p.ink3 }}>No e-sign envelopes on this listing yet.</div>}
            {envelopes.map((env, i) => (
              <div key={env.id} style={{ borderBottom: i === envelopes.length - 1 ? 'none' : `1px solid ${p.hair}` }}>
                <div className="bx-trow bx-1l" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '16px var(--bx-pad)', fontSize: 13, alignItems: 'center' }}>
                  <div className="bx-t" style={{ fontWeight: 500 }}>
                    {env.title}
                    {env.provider === 'docusign' && <span style={{ marginLeft: 8, fontSize: 12, color: p.ink3 }}>DocuSign</span>}
                  </div>
                  <div className="bx-x" style={{ color: p.ink3 }}>{env.recipient_count || (env.recipients || []).length} recipient{(env.recipient_count || (env.recipients || []).length) !== 1 ? 's' : ''}</div>
                  <div className="bx-s"><Pill tone={ENV_TONE[env.status] || 'neutral'} dark={dark}>{env.provider === 'docusign' && env.external_status === 'created' ? 'draft in DocuSign' : (env.status || 'draft')}</Pill></div>
                  <div style={{ fontFamily: F.mono, fontSize: 12, color: p.ink3 }}><DateYr d={env.created_at} /></div>
                </div>
                {(env.recipients || []).length > 0 && (
                  <div style={{ padding: '0 24px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {env.recipients.map((r, ri) => (
                      <div key={r.id || ri} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: p.surf2, borderRadius: 2, fontSize: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>{r.name}</div>
                          <div style={{ color: p.ink3, fontFamily: F.mono }}>{r.email}</div>
                        </div>
                        <Pill tone={r.status === 'signed' ? 'ok' : 'neutral'} dark={dark}>{r.status || 'pending'}</Pill>
                        {env.provider !== 'docusign' && r.signing_url && env.status !== 'completed' && (
                          <button onClick={() => copyLink(r.signing_url)} style={linkBtn}>Copy link</button>
                        )}
                      </div>
                    ))}
                    {env.provider === 'docusign' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {env.external_status === 'created' && (
                          <Btn variant="ghost" dark={dark} onClick={() => handleDocuSignAction(env, 'prepare')} disabled={!!dsBusy}>Open in DocuSign to send</Btn>
                        )}
                        <Btn variant="ghost" dark={dark} onClick={() => handleDocuSignAction(env, 'status')} disabled={!!dsBusy}>
                          {dsBusy === `status-${env.id}` ? 'Checking…' : 'Check DocuSign'}
                        </Btn>
                        {env.status === 'completed' && !env.signed_document_id && (
                          <Btn variant="primary" dark={dark} onClick={() => handleDocuSignAction(env, 'sync')} disabled={!!dsBusy}>File signed PDF</Btn>
                        )}
                        {env.signed_document_id && (
                          <span style={{ fontSize: 12, color: p.ink3 }}>Signed PDF with certificate is in Documents{env.signed_document_name ? `: ${env.signed_document_name}` : ''}</span>
                        )}
                      </div>
                    )}
                    {env.provider !== 'docusign' && env.status === 'completed' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {env.signed_record_filed ? (
                          <span style={{ fontSize: 12, color: p.ink3 }}>Signed record in Documents{env.signed_document_name ? `: ${env.signed_document_name}` : ''}</span>
                        ) : (
                          <Btn variant="ghost" dark={dark} onClick={() => handleFileSignedRecord(env)} disabled={!!dsBusy}>
                            {dsBusy === `record-${env.id}` ? 'Filing…' : 'File signed record'}
                          </Btn>
                        )}
                      </div>
                    )}
                    {env.provider !== 'docusign' && env.status === 'draft' && (
                      <div>
                        <Btn variant="ghost" dark={dark} onClick={() => handleSendEnvelope(env.id)} disabled={sendingId === env.id}>
                          {sendingId === env.id ? 'Sending…' : 'Send for signing'}
                        </Btn>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showDocModal} onOpenChange={(open) => { setShowDocModal(open); if (!open) { setDocError(''); setDocFile(null); setDocUploading(false); } }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <form onSubmit={handleAddDoc}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Upload document</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Upload a file to this listing.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>File</Over>
              <input name="file" type="file" style={{ ...inputStyle, padding: '8px 0' }}
                onChange={e => {
                  const f = e.target.files?.[0] || null;
                  setDocFile(f);
                  if (f && !docForm.name) setDocForm({ ...docForm, name: f.name.replace(/\.[^.]+$/, '') });
                }}/>
              {docFile && <div style={{ marginTop: 6, fontSize: 12, color: p.ink3 }}>{docFile.name}</div>}
            </div>
            <div><Over color={p.ink3}>Document name</Over>
              <input name="name" type="text" value={docForm.name} onChange={e => setDocForm({ ...docForm, name: e.target.value })} placeholder="e.g. Signed Agreement of Purchase"
                style={inputStyle}/>
            </div>
            <div><Over color={p.ink3}>Type</Over>
              <select name="type" value={docForm.type} onChange={e => setDocForm({ ...docForm, type: e.target.value })}
                style={{ ...inputStyle, background: p.bg }}>
                {['General','Agreement','Amendment','Disclosure','Waiver','Invoice','Other'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            {comp && comp.items.length > 0 && (
              <div><Over color={p.ink3}>Counts toward</Over>
                <select value={docForm.requirement_id || ''} onChange={e => setDocForm({ ...docForm, requirement_id: e.target.value })}
                  style={{ ...inputStyle, background: p.bg }}>
                  <option value="">Not a required document</option>
                  {comp.items.map(it => <option key={it.requirement_id} value={it.requirement_id}>{it.name}</option>)}
                </select>
              </div>
            )}
            {docError && <div style={{ fontSize: 13, color: T.brick }}>{docError}</div>}
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn type="button" variant="ghost" dark={dark} onClick={() => setShowDocModal(false)}>Cancel</Btn>
            <Btn type="submit" variant="primary" dark={dark} disabled={docUploading}>{docUploading ? 'Uploading…' : 'Upload'}</Btn>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectDoc} onOpenChange={o => { if (!o) setRejectDoc(null); }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Send back to the agent</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{rejectDoc?.name}. Say what needs fixing.</DialogDescription>
          </DialogHeader>
          <textarea rows={4} value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="e.g. Buyer's initials missing on page 3" style={{ ...inputStyle, resize: 'vertical' }}/>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setRejectDoc(null)}>Cancel</Btn>
            <Btn variant="accent" dark={dark} disabled={rejectNote.trim().length < 3}
              onClick={async () => { if (await handleDocAction(rejectDoc.id, 'reject', rejectNote.trim())) setRejectDoc(null); }}>Send back</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!exemptReq} onOpenChange={o => { if (!o) setExemptReq(null); }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Not applicable to this deal</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{exemptReq?.name}. Say why.</DialogDescription>
          </DialogHeader>
          <textarea rows={3} value={exemptReason} onChange={e => setExemptReason(e.target.value)} placeholder="e.g. Deposit held by the listing brokerage" style={{ ...inputStyle, resize: 'vertical' }}/>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setExemptReq(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={exemptReason.trim().length < 3 || compBusy}
              onClick={async () => { if (await compCall(`/compliance/${exemptReq.requirement_id}/exempt`, 'POST', { reason: exemptReason.trim() }, 'Marked not applicable')) setExemptReq(null); }}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCondModal} onOpenChange={setShowCondModal}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add condition</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Deadline from the offer, Toronto time.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Condition</Over>
              <select value={condForm.kind} onChange={e => setCondForm({ ...condForm, kind: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                {COND_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>Name on this deal (optional)</Over>
              <input type="text" value={condForm.label} onChange={e => setCondForm({ ...condForm, label: e.target.value })} placeholder="e.g. Financing, 5 banking days" style={inputStyle}/>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12 }}>
              <div><Over color={p.ink3}>Deadline date</Over>
                <input type="date" value={condForm.date} onChange={e => setCondForm({ ...condForm, date: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
              </div>
              <div><Over color={p.ink3}>Time</Over>
                <input type="time" value={condForm.time} onChange={e => setCondForm({ ...condForm, time: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
              </div>
            </div>
            <div><Over color={p.ink3}>Notes (optional)</Over>
              <input type="text" value={condForm.notes} onChange={e => setCondForm({ ...condForm, notes: e.target.value })} placeholder="e.g. Buyer's lender: TD" style={inputStyle}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowCondModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAddCondition} disabled={condBusy}>Add Condition</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveCond} onOpenChange={o => { if (!o) setResolveCond(null); }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Record outcome</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{resolveCond?.label}. This is permanent once saved.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>What happened</Over>
              <select value={resolveForm.outcome} onChange={e => setResolveForm({ ...resolveForm, outcome: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                <option value="fulfilled">Fulfilled</option>
                <option value="waived">Waived</option>
                <option value="lapsed">Lapsed (not met or waived in time)</option>
              </select>
            </div>
            {resolveForm.outcome !== 'lapsed' && (
              <div><Over color={p.ink3}>Signed notice or waiver (optional)</Over>
                <select value={resolveForm.document_id} onChange={e => setResolveForm({ ...resolveForm, document_id: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                  <option value="">None attached</option>
                  {docs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                {docs.length === 0 && <div style={{ fontSize: 12, color: p.ink4, marginTop: 6 }}>Upload it in Documents first.</div>}
              </div>
            )}
            <div><Over color={p.ink3}>Note (optional)</Over>
              <input type="text" value={resolveForm.note} onChange={e => setResolveForm({ ...resolveForm, note: e.target.value })} style={inputStyle}/>
            </div>
            {resolveForm.outcome === 'lapsed' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: p.ink2, cursor: 'pointer' }}>
                <input type="checkbox" checked={resolveForm.mark_deal_fallen_through} onChange={e => setResolveForm({ ...resolveForm, mark_deal_fallen_through: e.target.checked })} style={{ accentColor: T.brick }}/>
                Also mark this deal fallen through
              </label>
            )}
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setResolveCond(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleResolveCondition} disabled={condBusy}>Save outcome</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extendCond} onOpenChange={o => { if (!o) setExtendCond(null); }}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Change deadline</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>For a signed amendment. The old deadline stays on record.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, paddingTop: 20 }}>
            <div><Over color={p.ink3}>New date</Over>
              <input type="date" value={extendForm.date} onChange={e => setExtendForm({ ...extendForm, date: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
            </div>
            <div><Over color={p.ink3}>Time</Over>
              <input type="time" value={extendForm.time} onChange={e => setExtendForm({ ...extendForm, time: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setExtendCond(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleExtendCondition} disabled={condBusy}>Save deadline</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCheckModal} onOpenChange={setShowCheckModal}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add Checklist Item</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Add a task to the listing checklist.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Title</Over>
              <input type="text" value={checkForm.title} onChange={e => setCheckForm({ ...checkForm, title: e.target.value })} placeholder="e.g. Confirm deposit received" style={inputStyle}/>
            </div>
            <div><Over color={p.ink3}>Due date</Over>
              <input type="date" value={checkForm.due_date} onChange={e => setCheckForm({ ...checkForm, due_date: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
            </div>
            <div><Over color={p.ink3}>Assigned to</Over>
              <input type="text" value={checkForm.assigned_to} onChange={e => setCheckForm({ ...checkForm, assigned_to: e.target.value })} placeholder="Agent name or role" style={inputStyle}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowCheckModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAddCheckItem}>Add item</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEnvModal} onOpenChange={setShowEnvModal}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>New e-sign envelope</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Recipients sign at a secure Bricks link.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Envelope title</Over>
              <input type="text" value={envForm.title} onChange={e => setEnvForm({ ...envForm, title: e.target.value })} placeholder="e.g. Purchase Agreement — 123 Maple St" style={inputStyle}/>
            </div>
            <div><Over color={p.ink3}>Message to recipients</Over>
              <textarea value={envForm.message} onChange={e => setEnvForm({ ...envForm, message: e.target.value })} rows={3} placeholder="Please review and sign this document..."
                style={{ ...inputStyle, resize: 'vertical' }}/>
            </div>
            {envRecipients.map((r, i) => (
              <div key={i} style={{ padding: 14, border: `1px solid ${p.hair}`, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Over color={p.ink3}>Recipient {i + 1}</Over>
                  {envRecipients.length > 1 && (
                    <button onClick={() => setEnvRecipients(envRecipients.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.ink3 }}>Remove</button>
                  )}
                </div>
                <input type="text" value={r.name} onChange={e => setEnvRecipients(envRecipients.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Full name" style={{ ...inputStyle, marginTop: 0 }}/>
                <input type="email" value={r.email} onChange={e => setEnvRecipients(envRecipients.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))} placeholder="Email" style={{ ...inputStyle, marginTop: 0 }}/>
                <select value={r.role} onChange={e => setEnvRecipients(envRecipients.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))}
                  style={{ ...inputStyle, marginTop: 0, background: p.bg }}>
                  {['Signer','Buyer','Seller','Witness','Agent','Solicitor'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            ))}
            <button onClick={() => setEnvRecipients([...envRecipients, { name: '', email: '', role: 'Signer' }])}
              style={{ background: 'none', border: `1px dashed ${p.hairS}`, borderRadius: 2, padding: 10, cursor: 'pointer', color: p.ink3, fontFamily: F.display, fontSize: 13 }}>
              Add recipient
            </button>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowEnvModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreateEnvelope}>Create envelope</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDsModal} onOpenChange={setShowDsModal}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Send via DocuSign</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Sent from your office’s DocuSign{docusign?.account_name ? ` (${docusign.account_name})` : ''}. Signed PDFs file back to this deal.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Document (PDF from this deal)</Over>
              {docs.length === 0 ? (
                <div style={{ marginTop: 8, fontSize: 13, color: p.ink3 }}>Upload the PDF under Documents first.</div>
              ) : (
                <select value={dsForm.document_id} onChange={e => setDsForm({ ...dsForm, document_id: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                  {docs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              )}
            </div>
            <div><Over color={p.ink3}>Email subject</Over>
              <input type="text" value={dsForm.title} onChange={e => setDsForm({ ...dsForm, title: e.target.value })} placeholder="Please sign" style={inputStyle}/>
            </div>
            <div><Over color={p.ink3}>Message to signers</Over>
              <textarea value={dsForm.message} onChange={e => setDsForm({ ...dsForm, message: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }}/>
            </div>
            {dsRecipients.map((r, i) => (
              <div key={i} style={{ padding: 14, border: `1px solid ${p.hair}`, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Over color={p.ink3}>Signer {i + 1}</Over>
                  {dsRecipients.length > 1 && (
                    <button onClick={() => setDsRecipients(dsRecipients.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.ink3 }}>Remove</button>
                  )}
                </div>
                <input type="text" value={r.name} onChange={e => setDsRecipients(dsRecipients.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Full name" style={{ ...inputStyle, marginTop: 0 }}/>
                <input type="email" value={r.email} onChange={e => setDsRecipients(dsRecipients.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))} placeholder="Email" style={{ ...inputStyle, marginTop: 0 }}/>
                <select value={r.role} onChange={e => setDsRecipients(dsRecipients.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} style={{ ...inputStyle, marginTop: 0, background: p.bg }}>
                  {['Signer','Buyer','Seller','Witness','Agent','Solicitor'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            ))}
            <button onClick={() => setDsRecipients([...dsRecipients, { name: '', email: '', role: 'Signer' }])}
              style={{ background: 'none', border: `1px dashed ${p.hairS}`, borderRadius: 2, padding: 10, cursor: 'pointer', color: p.ink3, fontFamily: F.display, fontSize: 13 }}>
              Add signer
            </button>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: p.ink2, lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={dsForm.send_now} onChange={e => setDsForm({ ...dsForm, send_now: e.target.checked })} style={{ marginTop: 3, accentColor: T.brick }}/>
              <span>Send now. Signers place their own signature.</span>
            </label>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowDsModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleDocuSignSend} disabled={dsBusy === 'send' || docs.length === 0}>
              {dsBusy === 'send' ? 'Sending…' : dsForm.send_now ? 'Send via DocuSign' : 'Continue in DocuSign'}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCalModal} onOpenChange={setShowCalModal}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add showing or closing</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Saved on this listing.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Kind</Over>
              <select value={calForm.kind} onChange={e => setCalForm({ ...calForm, kind: e.target.value })} style={{ ...inputStyle, background: p.bg }}>
                <option value="showing">Showing</option>
                <option value="closing">Closing</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div><Over color={p.ink3}>Title</Over>
              <input type="text" value={calForm.title} onChange={e => setCalForm({ ...calForm, title: e.target.value })} placeholder="Optional — defaults from kind" style={inputStyle}/>
            </div>
            <div><Over color={p.ink3}>Starts</Over>
              <input type="datetime-local" value={calForm.starts_at} onChange={e => setCalForm({ ...calForm, starts_at: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
            </div>
            <div><Over color={p.ink3}>Ends</Over>
              <input type="datetime-local" value={calForm.ends_at} onChange={e => setCalForm({ ...calForm, ends_at: e.target.value })} style={{ ...inputStyle, background: p.bg }}/>
            </div>
            <div><Over color={p.ink3}>Location</Over>
              <input type="text" value={calForm.location} onChange={e => setCalForm({ ...calForm, location: e.target.value })} placeholder={tx.property_address || 'Listing address'} style={inputStyle}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowCalModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAddEvent}>Add event</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
